import { z } from 'zod'

import { ScraperMarketplace } from '@/client'

/**
 * The view state the Scraped data page reads from the URL.
 *
 * Every field optional with `.catch(undefined)`, as everywhere in this app: a
 * query string is user input, and a default written into every URL makes the
 * clean view and the filtered one indistinguishable. Defaults are applied at
 * read time instead — including the default 30-day range, which would be
 * wrong in a link opened next week if it were written in.
 */

export const SCRAPED_TABS = ['reviews', 'ads'] as const
export type ScrapedTab = (typeof SCRAPED_TABS)[number]

const marketplaces = Object.values(ScraperMarketplace) as [
  ScraperMarketplace,
  ...ScraperMarketplace[],
]

/** `YYYY-MM-DD`, the shape `<input type="date">` and the API both speak. */
const isoDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/)

export const PAGE_SIZES = [20, 50, 100] as const

export const scrapedDataSearchSchema = z.object({
  tab: z
    .enum(SCRAPED_TABS)
    .optional()
    .catch(undefined)
    .describe(
      "Which scraper's rows are shown: product reviews or search-results ads; reviews by default.",
    ),
  marketplace: z
    .enum(marketplaces)
    .optional()
    .catch(undefined)
    .describe('Only rows scraped from this marketplace; every marketplace when absent.'),
  store: z
    .number()
    .int()
    .positive()
    .optional()
    .catch(undefined)
    .describe('Only rows for this store, by id; every store when absent.'),
  from: isoDay
    .optional()
    .catch(undefined)
    .describe('First scrape day, YYYY-MM-DD (UTC); the last 30 days when neither date is set.'),
  to: isoDay
    .optional()
    .catch(undefined)
    .describe('Last scrape day, YYYY-MM-DD (UTC), inclusive; today when neither date is set.'),
  q: z
    .string()
    .trim()
    .min(1)
    .max(128)
    .optional()
    .catch(undefined)
    .describe('Text matched against the ASIN or FSN on reviews, or the search term on ads.'),
  page: z
    .number()
    .int()
    .positive()
    .optional()
    .catch(undefined)
    .describe('The page of rows; 1 by default.'),
  limit: z
    .number()
    .int()
    .min(1)
    .max(100)
    .optional()
    .catch(undefined)
    .describe('Rows per page (the menu offers 20, 50 and 100); 20 by default.'),
  /** The row whose detail is open: a listing id on reviews, a search id on ads. */
  open: z
    .number()
    .int()
    .positive()
    .optional()
    .catch(undefined)
    .describe('The row whose detail dialog is open: a listing id on reviews, a search id on ads.'),
})

export type ScrapedDataSearch = z.infer<typeof scrapedDataSearchSchema>

export const SCRAPED_SEARCH_DEFAULTS = { tab: 'reviews', page: 1, limit: 20 } as const

/** Drop any value that equals its default, so a URL carries only what differs. */
export function withoutScrapedSearchDefaults(search: ScrapedDataSearch): ScrapedDataSearch {
  const pruned: ScrapedDataSearch = {}
  if (search.tab !== undefined && search.tab !== SCRAPED_SEARCH_DEFAULTS.tab) {
    pruned.tab = search.tab
  }
  if (search.page !== undefined && search.page !== SCRAPED_SEARCH_DEFAULTS.page) {
    pruned.page = search.page
  }
  if (search.limit !== undefined && search.limit !== SCRAPED_SEARCH_DEFAULTS.limit) {
    pruned.limit = search.limit
  }
  if (search.marketplace !== undefined) pruned.marketplace = search.marketplace
  if (search.store !== undefined) pruned.store = search.store
  if (search.from !== undefined) pruned.from = search.from
  if (search.to !== undefined) pruned.to = search.to
  if (search.q !== undefined) pruned.q = search.q
  if (search.open !== undefined) pruned.open = search.open
  return pruned
}

/**
 * The UTC day it is now, as `YYYY-MM-DD`.
 *
 * UTC because that is how the API bounds a day; the scrapers run on a UTC
 * schedule, so "today" means the same rows for everyone.
 */
export function todayUtc(now: Date = new Date()): string {
  return now.toISOString().slice(0, 10)
}

/** How far back both tabs look when the URL names no dates. */
export const DEFAULT_RANGE_DAYS = 30

/**
 * The date range to ask for: what the URL says, or with no dates at all, the
 * last 30 UTC days up to and including today — recent enough to load quickly,
 * long enough to show a trend. Applied at read time, never written into the
 * URL, so a link opened next week still means "the last 30 days".
 */
export function effectiveRange(
  search: ScrapedDataSearch,
  now: Date = new Date(),
): { from?: string; to?: string } {
  if (search.from === undefined && search.to === undefined) {
    const start = new Date(now)
    start.setUTCDate(start.getUTCDate() - (DEFAULT_RANGE_DAYS - 1))
    return { from: todayUtc(start), to: todayUtc(now) }
  }
  return { from: search.from, to: search.to }
}
